import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditAttachment } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { CardAttachment } from "@shared/models/card-stack"
import Patch from "@shared/models/patch-updater"
import { assign, find, get } from "lodash-es"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStackId: string
  sectionId: string
  attachment: CardAttachment
}

export const LESSON_CARD_REMOVE_ATTACHMENT = {
  name: "LESSON_CARD_REMOVE_ATTACHMENT",

  patches(payload: Payload) {
    let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
      id: payload.sectionId,
    }).pull("embedded", "attributes.attachments", { id: payload.attachment.id }).patch

    return [patch]
  },

  undoPatches(payload: Payload) {
    let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
      id: payload.sectionId,
    }).addToSet("embedded", "attributes.attachments", payload.attachment).patch

    return [patch]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let models = defaultLessonModels(payload, findFn, userId)
    let card = find(get(models, "cardStack.attributes.cards"), {
      id: payload.sectionId,
    })
    let cardTitle = get(card, "attributes.title")
    let attachment = payload.attachment
    let attachmentTitle = get(attachment, "title")
    let context = defaultLessonContext(payload, models)
    let newContext = assign({}, context, {
      cardTitle: cardTitle,
      attachmentTitle,
    })
    let newEvent: LessonEditAttachment = {
      ...createBlankEvent(),
      root_id: models.planbook?.id ?? null,
      root_type: "planbook",
      date_plan_scheduled_for: models.dateStringForCardStack,
      primary_object_id: payload.cardStackId,
      primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
      primary_object_type: "lesson",
      primary_object_specifier: "attachments",

      adverbial_verb: "removing",
      adverbial_object_type: "attachment",
      adverbial_object_specifier: null,
      adverbial_object_id: payload.attachment.id,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    }

    return {
      title: "Removed a File",
      titleForAnalytics: "Attachment - Remove",
      description: `Removed 1 file from "${cardTitle}" card`,
      context: newContext,
      activity: defaultLessonActivity(payload, models, "removed"),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default LESSON_CARD_REMOVE_ATTACHMENT
